// Inline SVG icon registry — the single swap point for the Carbon icon set (BR-32).
// 24px placeholder glyphs on a 24x24 grid; sparkle and play are fill glyphs, the
// rest are stroke. No icon font, no runtime asset requests (CSP/PWA-safe, NFR-3).

export type IconName =
  | 'arrow-back'
  | 'undo'
  | 'redo'
  | 'hint'
  | 'eye'
  | 'flag'
  | 'restore'
  | 'restart'
  | 'sparkle'
  | 'help'
  | 'sliders'
  | 'timer'
  | 'lock'
  | 'check'
  | 'play'
  | 'close'
  | 'chevron-right'
  | 'login'
  | 'logout';

export interface IconGlyph {
  viewBox: '0 0 24 24';
  pathData: string;
  paint: 'stroke' | 'fill';
}

export const ICON_REGISTRY: Record<IconName, IconGlyph> = {
  'arrow-back': {
    viewBox: '0 0 24 24',
    pathData: 'M19 12H5M12 19l-7-7 7-7',
    paint: 'stroke',
  },
  undo: {
    viewBox: '0 0 24 24',
    pathData: 'M1 4v6h6M3.51 15a9 9 0 1 0 2.13-9.36L1 10',
    paint: 'stroke',
  },
  redo: {
    viewBox: '0 0 24 24',
    pathData: 'M23 4v6h-6M20.49 15a9 9 0 1 1-2.12-9.36L23 10',
    paint: 'stroke',
  },
  hint: {
    viewBox: '0 0 24 24',
    pathData:
      'M9 18h6M10 21h4M12 3a6 6 0 0 1 4 10.5c-.7.7-1 1.5-1 2.5H9c0-1-.3-1.8-1-2.5A6 6 0 0 1 12 3z',
    paint: 'stroke',
  },
  eye: {
    viewBox: '0 0 24 24',
    pathData:
      'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zM12 9a3 3 0 1 0 0 6 3 3 0 0 0 0-6z',
    paint: 'stroke',
  },
  flag: {
    viewBox: '0 0 24 24',
    pathData:
      'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7',
    paint: 'stroke',
  },
  restore: {
    viewBox: '0 0 24 24',
    pathData: 'M3 3v5h5M3 8a9 9 0 1 0 2.13-5.4L3 8',
    paint: 'stroke',
  },
  restart: {
    viewBox: '0 0 24 24',
    pathData: 'M21 12a9 9 0 1 1-2.64-6.36L21 8M21 3v5h-5',
    paint: 'stroke',
  },
  sparkle: {
    viewBox: '0 0 24 24',
    pathData: 'M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4z',
    paint: 'fill',
  },
  help: {
    viewBox: '0 0 24 24',
    pathData:
      'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01',
    paint: 'stroke',
  },
  sliders: {
    viewBox: '0 0 24 24',
    pathData:
      'M4 21v-7M4 10V3M12 21v-9M12 8V3M20 21v-5M20 12V3M1 14h6M9 8h6M17 16h6',
    paint: 'stroke',
  },
  timer: {
    viewBox: '0 0 24 24',
    pathData: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 6v6l4 2',
    paint: 'stroke',
  },
  lock: {
    viewBox: '0 0 24 24',
    pathData:
      'M5 11h14a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2zM7 11V7a5 5 0 0 1 10 0v4',
    paint: 'stroke',
  },
  check: {
    viewBox: '0 0 24 24',
    pathData: 'M20 6L9 17l-5-5',
    paint: 'stroke',
  },
  play: {
    viewBox: '0 0 24 24',
    pathData:
      'M8 5.14v13.72a1 1 0 0 0 1.5.86l11-6.86a1 1 0 0 0 0-1.72l-11-6.86a1 1 0 0 0-1.5.86z',
    paint: 'fill',
  },
  close: {
    viewBox: '0 0 24 24',
    pathData: 'M18 6L6 18M6 6l12 12',
    paint: 'stroke',
  },
  'chevron-right': {
    viewBox: '0 0 24 24',
    pathData: 'M9 18l6-6-6-6',
    paint: 'stroke',
  },
  login: {
    viewBox: '0 0 24 24',
    pathData: 'M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3',
    paint: 'stroke',
  },
  logout: {
    viewBox: '0 0 24 24',
    pathData: 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
    paint: 'stroke',
  },
};

export function getIcon(name: string): IconGlyph | undefined {
  return ICON_REGISTRY[name as IconName];
}
